import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, TrendingUp, Award, Search, Mail, Download, MessageCircle, Calendar, Clock, ArrowRight, Zap, FileBarChart, Send, HelpCircle, CheckCircle, AlertCircle, Activity, Filter, ExternalLink, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
export function AdminHub() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [emailToast, setEmailToast] = useState('');
  const [reminderLearner, setReminderLearner] = useState('');
  const [isReminderDropdownOpen, setIsReminderDropdownOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d'>('30d');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reminderDropdownRef = useRef<HTMLDivElement>(null);
  const incompleteLearners = [{
    value: 'rachel-patel',
    name: 'Rachel Patel',
    email: 'rachel.patel@example.com'
  }, {
    value: 'david-nguyen',
    name: 'David Nguyen',
    email: 'd.nguyen@example.com'
  }, {
    value: 'karen-brooks',
    name: 'Karen Brooks',
    email: 'k.brooks@example.com'
  }, {
    value: 'marcus-hall',
    name: 'Marcus Hall',
    email: 'marcus.h@example.com'
  }, {
    value: 'tanya-foster',
    name: 'Tanya Foster',
    email: 'tanya.f@example.com'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target as Node)) {
        setIsReminderDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isReminderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isReminderDropdownOpen]);
  const navigationCards = [{
    title: 'Analytics Dashboard',
    description: 'View detailed performance metrics and insights',
    icon: BarChart3,
    color: 'from-[#00638C] to-[#30718D]',
    stats: '248 learners tracked',
    link: '/analytics'
  }, {
    title: 'User Management',
    description: 'Manage learner accounts and permissions',
    icon: UserCog,
    color: 'from-[#30718D] to-[#265a70]',
    stats: '18 pending invites',
    link: '/assessment'
  }, {
    title: 'Reports',
    description: 'Generate and download custom reports',
    icon: FileText,
    color: 'from-[#007558] to-[#005a44]',
    stats: 'Last report: 1 hour ago',
    link: '/feedback'
  }, {
    title: 'Insights & Actions',
    description: 'Prioritize learner support with AI-powered skill insights',
    icon: Zap,
    color: 'from-[#DB3725] to-[#DB3725]',
    stats: '4 priority insights',
    link: '/insights-actions'
  }];
  const sendAccountCreationEmail = () => {
    const recipient = reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : null;
    setEmailToast(recipient ? `Account creation email sent to ${recipient}.` : 'Account creation email sent to all 5 incomplete learners.');
  };
  useEffect(() => {
    if (!emailToast) return;
    const timeoutId = window.setTimeout(() => setEmailToast(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [emailToast]);
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1763" data-mp="3a274vv0obrnx">
      {/* Email Sent Toast */}
      <AnimatePresence data-id="element-1764" data-mp="mw2hi2lwyark">
        {emailToast && <motion.div role="status" aria-live="polite" initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-20 left-1/2 z-50 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-3 rounded-lg bg-[#007558] px-6 py-3 text-white shadow-lg" data-id="element-1765" data-mp="mwvqzbc9bede">
            <CheckCircle size={20} className="flex-shrink-0" data-id="element-1766" data-mp="15rk8k3haabww" />
            <span className="font-medium" data-id="element-1767" data-mp="10u0x1bbz0d8n">{emailToast}</span>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1768" data-mp="1aklqylgp8tl8">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1769" data-mp="1al5bm9xqhw2y">
          <div className="flex items-center h-16" data-id="element-1770" data-mp="1ojlhxzropkkk">
            {/* Logo */}
            <div className="text-[#E53E3E] font-bold text-xl" data-id="element-1771" data-mp="1jljxbaem1d6z">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1772" data-mp="236k6nmz5q0vl">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-[#00638C] border-b-2 border-[#00638C] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1773" data-mp="2866hxqvfd71r">
                <Home size={18} data-id="element-1774" data-mp="1y6fm9lv4om03" />
                <span className="hidden sm:inline" data-id="element-1775" data-mp="1y9trr2z39bbr">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1776" data-mp="1kej0dzk1z8c5">
                <BarChart3 size={18} data-id="element-1777" data-mp="1fgzmx1v6izl9" />
                <span className="hidden sm:inline" data-id="element-1778" data-mp="1fi0vmzoq39zt">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1779" data-mp="11j19byoon76t">
                <FileText size={18} data-id="element-1780" data-mp="38835efkifkie" />
                <span className="hidden sm:inline" data-id="element-1781" data-mp="33es22oewplv8">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1782" data-mp="2yfxgxftgvl7y">
                <UserCog size={18} data-id="element-1783" data-mp="3r3jmuuq631xz" />
                <span className="hidden sm:inline" data-id="element-1784" data-mp="3r2ia8lti67e5">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1785" data-mp="3h5x05m2mm63g">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1786" data-mp="3d5d6ish9hkm2">
                <Bell size={20} data-id="element-1787" data-mp="21p52snq3g38t" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1788" data-mp="21sapxxf5wv1w"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1789" data-mp="l97n1cvvhz0q">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1790" data-mp="l9qu1yjzj2zz">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1791" data-mp="v151csi6h73o">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1792" data-mp="14vn8k7mszks8">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1793" data-mp="1dyo9zv0q4npk" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1794" data-mp="3arq1y0h3o4z6">
                    <div className="py-2" data-id="element-1795" data-mp="3frcj2l01u5d9">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1796" data-mp="1lgvwmmiuqbh2">
                        <Users size={18} className="text-gray-600" data-id="element-1797" data-mp="1v6z05gnln7tn" />
                        <span className="font-medium text-gray-900" data-id="element-1798" data-mp="1q8477lvx42vt">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1799" data-mp="12kmmubezg72v">
                        <Home size={18} className="text-gray-600" data-id="element-1800" data-mp="1ch95ctju6m1q" />
                        <span className="font-medium text-gray-900" data-id="element-1801" data-mp="17hw8dfz93ylf">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1802" data-mp="2nubbsvs81v86">
                        <UserCog size={18} className="text-gray-600" data-id="element-1803" data-mp="4pk0hv75ddr6" />
                        <span className="font-medium text-gray-900" data-id="element-1804" data-mp="4reojidptlzh">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-1805" data-mp="3rvb9v3acs33r"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1806" data-mp="n6y9grlzxz5">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1807" data-mp="odt0usiugq1r">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1808" data-mp="1c3dp9qxrofif"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1809" data-mp="q123k9zswh0i"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1810" data-mp="pyz5r3sepjl8"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1811" data-mp="uy2ccso1bxvb">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1812" data-mp="c10va9h7i7b0">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1813" data-mp="3ti27qzql07iz">
          <div data-id="element-1814" data-mp="2bhcf3thudg5">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D] mb-1" data-id="element-1815" data-mp="2ay50h1gz7gd">
              Administrator
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1816" data-mp="3khm49q9u90e4">
              Welcome Back, Linda
            </h1>
          </div>
          <button className="text-[#DB3725] hover:text-[#c02f1f] font-medium text-sm transition-colors self-start sm:self-auto flex items-center gap-1.5" data-id="element-1817" data-mp="35udril0vsn01">
            <Mail size={14} data-id="element-1818" data-mp="30wlwhi4xwkrm" />
            Contact support
          </button>
        </div>

        {/* Company & Send Email Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-1819" data-mp="3esqtoeb0uekv">
          {/* Company Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col" data-phoebe-tour="company-info" data-id="element-1820" data-mp="3ot9kxu4x0xct">
            <div className="flex items-center gap-3 mb-4" data-id="element-1821" data-mp="be3gssbw2as4">
              <div className="w-8 h-8 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-1822" data-mp="bempdifnfmth">
                <Building2 size={16} className="text-[#30718D]" data-id="element-1823" data-mp="6d75vut4dod7" />
              </div>
              <div data-id="element-1824" data-mp="3092xkrxkpaew">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1825" data-mp="305f4tuzycs18">
                  Company Name
                </div>
                <div className="text-base font-semibold text-gray-900" data-id="element-1826" data-mp="396vdnyb4cm3c">
                  Lorem Ipsum
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-1827" data-mp="3ixpzx2od7los">
              <div className="flex items-center gap-3" data-id="element-1828" data-mp="3ixhpdhfpssgr">
                <div className="w-8 h-8 rounded-lg bg-[#007558]/10 flex items-center justify-center flex-shrink-0" data-id="element-1829" data-mp="3nwb1b5fcsbet">
                  <Users size={16} className="text-[#007558]" data-id="element-1830" data-mp="3swyu9wn572bo" />
                </div>
                <div data-id="element-1831" data-mp="sefljeszsa0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1832" data-mp="qtns1jvinfu">
                    Registered Learners
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1833" data-mp="22j83qv2uxhtm">
                    4 Learners
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3" data-id="element-1834" data-mp="1q98n1j2d7678">
              <div className="flex items-center gap-3" data-id="element-1835" data-mp="1q87a191sg9mv">
                <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-1836" data-mp="1la5f9qv02lvk">
                  <Clock size={16} className="text-[#DB3725]" data-id="element-1837" data-mp="1h8tvi1yt4uva" />
                </div>
                <div data-id="element-1838" data-mp="2dyuibvfm919y">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1839" data-mp="2dzclzd4lyhrw">
                    Pending
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1840" data-mp="290jd5y09ney2">
                    5 Learners
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Send Account Creation Email */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2" data-phoebe-tour="send-email" data-id="element-1841" data-mp="tj9dpmee43nl">
            <div className="flex items-center gap-3 mb-4" data-id="element-1842" data-mp="23gxrc09o4g8i">
              <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-1843" data-mp="27hhnb0ii9dt2">
                <Mail size={20} className="text-[#30718D]" data-id="element-1844" data-mp="2v72hkfk3zxhn" />
              </div>
              <div data-id="element-1845" data-mp="2v4zmhh43ne62">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1846" data-mp="305wzzge5zk18">
                  Send Account Creation Email
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1847" data-mp="2m5chklc5vq89">
                  Remind learners to complete account setup.{' '}
                  <span className="font-semibold text-[#DB3725]" data-id="element-1848" data-mp="2r3xiyo35gfya">
                    5 pending
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-end" data-id="element-1849" data-mp="1frd598i7x2v0">
              <div className="relative flex-1" ref={reminderDropdownRef} data-id="element-1850" data-mp="1fnz586x115qq">
                <button onClick={() => {
                setIsReminderDropdownOpen(!isReminderDropdownOpen);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-1851" data-mp="177hziisswghi">
                  <Users size={18} className="text-[#30718D]" data-id="element-1852" data-mp="oik0p7mjruge" />
                  <span className="flex-1 text-left text-gray-700" data-id="element-1853" data-mp="oj3ce6lltmlz">
                    {reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : 'All incomplete learners (5)'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isReminderDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1854" data-mp="5sc0oye59k4f" />
                </button>

                {isReminderDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-1855" data-mp="20v8idlrfvnde">
                    <button onClick={() => {
                  setReminderLearner('');
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${reminderLearner === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1856" data-mp="2al1ssjfuhwg2">
                      All incomplete learners
                    </button>
                    {incompleteLearners.map(learner => <button key={learner.value} onClick={() => {
                  setReminderLearner(learner.value);
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${reminderLearner === learner.value ? 'bg-[#30718D]/5' : ''}`} data-id="element-1857" data-mp="32cq0gsnohusk">
                        <div className={`font-medium text-sm ${reminderLearner === learner.value ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-1858" data-mp="33o2v2rk9tur6">
                          {learner.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1859" data-mp="2tobzsnnvzbq1">
                          {learner.email}
                        </div>
                      </button>)}
                  </div>}
              </div>
              <button onClick={sendAccountCreationEmail} className="px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1860" data-mp="mz74xa4u3rsf">
                <Send size={16} data-id="element-1861" data-mp="127wxa2xix9sn" />
                Send
              </button>
            </div>
          </motion.div>
        </div>

        {/* Performance Snapshot */}
        <div data-phoebe-tour="dashboard" className="mb-8" data-id="element-1862" data-mp="shbzghpoc9as">
          <div className="flex items-center gap-2 mb-4" data-id="element-1863" data-mp="sepos97hldoc">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-1864" data-mp="xevdphmdgm2q">
              Performance Snapshot
            </h2>
            <span data-phoebe-tour="ai-insight-icon" className="inline-flex" data-id="element-1865" data-mp="ih2y6pgtrxoj">
            <AIInsightPopover align="left" headline="Participation is strong but completion is slipping" insightBeforeMetric="80% of learners were active this month, yet only" highlightedMetric="10%" insightAfterMetric=" have finished every module." explanation="The opportunity sits with active learners who have one or two modules left — a focused reminder should close the gap." insightId={12} data-id="element-1866" data-mp="nhza9qbbyq51" />
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1867" data-mp="2iwwdsnqy098n">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-1868" data-mp="2e1qorg28uegc">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1869" data-mp="2wshwkd6l1wsm">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1870" data-mp="3ap57xkza8pi2">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1871" data-mp="36ourtfhs0iph">
                    55
                  </span>
                  <span className="align-middle text-sm" data-id="element-1872" data-mp="1gcx4tkct52b7">
                    of your invited learners have
                    <br data-id="element-1873" data-mp="1cc4usow42tdv" />
                    completed registration
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#007558] text-sm font-medium" data-id="element-1874" data-mp="jmqygca6qczk">
                  <span className="text-xs" data-id="element-1875" data-mp="ep8r2qbtg5to">▲</span>
                  <span data-id="element-1876" data-mp="tewv3zbdtil3">16% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1877" data-mp="ofk3705nllbn"></div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1878" data-mp="oxzfl2omn7g">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1879" data-mp="1l4jgitl3hdap">
                <div className="flex items-start justify-between mb-3 gap-2" data-id="element-1880" data-mp="1h3zjrr4fobp3">
                  <div className="text-gray-700 leading-relaxed" data-id="element-1881" data-mp="1h627g4i45sqn">
                    <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1882" data-mp="1omgzwg6teor1">
                      {timePeriod === '7d' ? '65%' : '80%'}
                    </span>
                    <span className="align-middle text-sm" data-id="element-1883" data-mp="1epuarf7ghbj7">
                      of your learners have
                      <br data-id="element-1884" data-mp="1jnm4mernv7q1" />
                      spent time learning this{' '}
                      {timePeriod === '7d' ? 'week' : 'month'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 flex-shrink-0" data-id="element-1885" data-mp="2cdmaogxwequh">
                    <div className="flex items-center bg-gray-100 rounded-md p-px" data-id="element-1886" data-mp="1xo6nfaxkt3jj">
                      <button onClick={() => setTimePeriod('7d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '7d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1887" data-mp="1xm3yyvc2nigx">
                        7 Days
                      </button>
                      <button onClick={() => setTimePeriod('30d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '30d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1888" data-mp="34x6ux8hrkjwr">
                        30 Days
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-sm font-medium ${timePeriod === '7d' ? 'text-[#007558]' : 'text-[#007558]'}`} data-id="element-1889" data-mp="2h8noyzm6sj7u">
                  <span className="text-xs" data-id="element-1890" data-mp="28aldtr8qoa6u">▲</span>
                  <span data-id="element-1891" data-mp="23a7f4zt12clh">
                    {timePeriod === '7d' ? '12% vs prior 7 days' : '20% vs 30 days ago'}
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1892" data-mp="dtv5w2bovmyt"></div>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1893" data-mp="ir3m3xda1gxf">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1894" data-mp="2gmhudvdjr8">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1895" data-mp="43ilca3vjtso">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1896" data-mp="415wdczp8j64">
                    10%
                  </span>
                  <span className="align-middle text-sm" data-id="element-1897" data-mp="38i0cmkj8um22">
                    of your learners have
                    <br data-id="element-1898" data-mp="3cik53asvh1hv" />
                    completed all skill modules
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#DB3725] text-sm font-medium" data-id="element-1899" data-mp="2tfidb54ff3m9">
                  <span className="text-xs" data-id="element-1900" data-mp="2ogf8nlscyzet">▼</span>
                  <span data-id="element-1901" data-mp="2odj9xv8os6pb">20% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#DB3725]" data-id="element-1902" data-mp="336d1wg55afav"></div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div data-phoebe-tour="navigation-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-1903" data-mp="3c45gtj6yfkxq">
          {navigationCards.map((card, index) => {
          const Icon = card.icon;
          return <Link key={index} to={card.link} className="h-full" data-id="element-1904" data-mp="3m2meez0eexsr" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.1
            }} className="group flex h-full flex-col bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1905" data-mp="3h3rv7vyir784">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-1906" data-mp="3on14zqaxt7xp">
                    <Icon size={24} className="text-white" data-id="element-1907" data-mp="3flkulik4h5th" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#00638C] transition-colors" data-id="element-1908" data-mp="3jp94w9052fif">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 flex-1" data-id="element-1909" data-mp="3amzstep82e2x">
                    {card.description}
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-2" data-id="element-1910" data-mp="13xuyq3dzuy6d">
                    <span className="text-xs font-medium text-gray-500" data-id="element-1911" data-mp="18vd5po8gb3bq">
                      {card.stats}
                    </span>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-[#00638C] group-hover:translate-x-1 transition-all" data-id="element-1912" data-mp="17d68lkt9jk1u" />
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        {/* Contact Support - Full Width Horizontal */}
        <motion.div data-phoebe-tour="support" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-gradient-to-br from-[#30718D] to-[#265a70] rounded-xl p-6 shadow-lg text-white mb-8" data-id="element-1913" data-mp="1q4fivwauycuh">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-1914" data-mp="28x9xgp4kk2y6">
            <div className="flex items-center gap-4" data-id="element-1915" data-mp="244q9cy899pf4">
              <div className="w-12 h-12 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1916" data-mp="2ce5uhb3tp5hh">
                <HelpCircle size={24} className="text-white" data-id="element-1917" data-mp="2gezis5l6gmaw" />
              </div>
              <div data-id="element-1918" data-mp="2leb0bgtdbvtj">
                <h3 className="text-xl font-bold mb-1" data-id="element-1919" data-mp="2ldsubsgwm38f">Need Help?</h3>
                <p className="text-sm text-white/70 max-w-2xl" data-id="element-1920" data-mp="2qdnn61k8p2w4">
                  Our support team is available 24/7 to help you with any
                  questions or issues you may have.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end justify-center gap-3 w-full md:w-auto" data-id="element-1921" data-mp="2zd2ais540xce">
              <button className="w-full md:w-auto px-6 py-3 bg-white text-[#30718D] rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-1922" data-mp="3463sxa4izf0q">
                <Mail size={16} data-id="element-1923" data-mp="3e5cizrw9s9ht" />
                Email Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}